import { useState, useMemo } from "react";
import { motion } from "framer-motion";

const COPY = {
  okada_rider: {
    title: "Bike Details",
    subtitle: "Please provide your bike information",
    nameLabel: "Bike Name/Model *",
    namePlaceholder: "e.g., Honda CG 125, Yamaha DT 125",
    nameHint: "Enter the make and model of your bike",
    nameKey: "bikeName",
    licenseHint: "Enter your bike's license plate number",
    licensePlaceholder: "e.g., M-1234-21",
  },
  daily_commuter: {
    title: "Vehicle Details",
    subtitle: "Please provide your vehicle information",
    nameLabel: "Vehicle Name/Model *",
    namePlaceholder: "e.g., Toyota Corolla, Honda Civic",
    nameHint: "Enter the make and model of your vehicle",
    nameKey: "vehicleName",
    licenseHint: "Enter your vehicle's license plate number",
    licensePlaceholder: "e.g., GR-1234-21",
  },
};

export default function OkadaForm({
  onSubmit,
  onBack,
  initialData = {},
  variant = "okada_rider",
}) {
  const copy = COPY[variant] || COPY.okada_rider;
  const [vehicleName, setVehicleName] = useState(
    initialData[copy.nameKey] || initialData.bikeName || initialData.vehicleName || ""
  );
  const [licensePlate, setLicensePlate] = useState(initialData.licensePlate || "");

  const isValid = useMemo(() => {
    return vehicleName.trim() && licensePlate.trim();
  }, [vehicleName, licensePlate]);

  const handleSubmit = () => {
    onSubmit({
      [copy.nameKey]: vehicleName.trim(),
      licensePlate: licensePlate.trim(),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="mb-6 text-center">
        <h3 className="mb-2 text-xl font-bold text-gray-900">{copy.title}</h3>
        <p className="text-sm text-gray-600">{copy.subtitle}</p>
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-800">
          {copy.nameLabel}
        </label>
        <input
          type="text"
          placeholder={copy.namePlaceholder}
          value={vehicleName}
          onChange={(e) => setVehicleName(e.target.value)}
          className="px-4 py-3 w-full rounded-xl border border-gray-300 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />
        <p className="mt-1 text-xs text-gray-500">{copy.nameHint}</p>
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-800">
          License Plate Number *
        </label>
        <input
          type="text"
          placeholder={copy.licensePlaceholder}
          value={licensePlate}
          onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
          className="px-4 py-3 w-full rounded-xl border border-gray-300 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />
        <p className="mt-1 text-xs text-gray-500">{copy.licenseHint}</p>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid}
          className="flex-1 px-4 py-3 font-semibold text-white bg-emerald-600 rounded-xl transition-colors hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    </motion.div>
  );
}
